import { formatDisplayDate, formatDisplayDateTime } from './dateDisplay';
import { formatLocation, isLocationValue } from './elementFormat/location';
import { formatCurrency, isCurrencyValue } from './currency';
import { DELETED_TAG_LABEL } from './tags';
import { itemTagTitleFor } from './tagTitleMemory';
import { normalizeId } from './ids';

/**
 * Vocabulary and value rendering for change-log entries (keepr-api
 * docs/SCHEMA.md § History). The API already renders element labels, choice
 * labels, lookup titles and the `summary` sentence; what is left to the client
 * is the product's short verb for an action, the tag color for its kind, and
 * how a raw stored value reads in a Before/After cell (dates through the
 * user's display preference, which only the client knows).
 */

export const TYPE_LABELS = {
	item: 'Item', collection: 'Collection', card: 'Card', grant: 'Grant', group: 'Group',
	filter: 'Saved filter', automation: 'Automation', shareLink: 'Share link', tag: 'Tag',
	itemRelationship: 'Relationship', event: 'Event', user: 'Profile',
	preferences: 'Preferences', favorite: 'Favorite', apiKey: 'API key', file: 'File', attachment: 'Attachment', maintenance: 'Maintenance',
	elementSet: 'Element set',
	// A saved chart (keepr-api utils/chartUtils.js, charts A3). A colleague's
	// personal chart never reaches a feed: the API leaves its rows out.
	chart: 'Chart',
	// A dashboard (keepr-api utils/dashboardUtils.js, charts A6), the same
	// way: a colleague's personal dashboard never reaches a feed.
	dashboard: 'Dashboard',
};

// The types a collection feed can be narrowed to, in the order a manager
// would look for them. `tag` finds the collection's own tags' rows (v: 2 —
// docs/plans/tags.md §3.9). The tags that came before them left no trace
// here: their rows are never listed (ruled b08, 2026-09-30 — the API leaves
// them out of every History list).
export const FEED_TYPE_OPTIONS = [
	'item', 'card', 'collection', 'grant', 'group', 'automation', 'filter', 'chart', 'dashboard', 'shareLink',
	'tag', 'itemRelationship', 'file', 'attachment', 'event',
].map(value => ({ value, label: TYPE_LABELS[value] }));

export const ADMIN_TYPE_OPTIONS = Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label }));

const VERBS = {
	create: 'Created', update: 'Updated', delete: 'Deleted', restore: 'Restored', revert: 'Reverted',
	recompute: 'Recomputed', relate: 'Related', unrelate: 'Unrelated',
	attach: 'Attached file', detach: 'Removed file', archive: 'Archived', unarchive: 'Unarchived',
	transfer: 'Transferred', promote: 'Promoted', demote: 'Demoted',
	sync: 'Synced', enable: 'Enabled', disable: 'Disabled', revoke: 'Revoked', add: 'Added', remove: 'Removed',
	run: 'Ran',
	// Intake (G9): a submission through a public form, and its triage.
	intake: 'Submitted', accept: 'Accepted', reject: 'Rejected',
	// The migration of stored filters (deterministic filters F5, keepr-api
	// utils/filterNameMigration.js): `<type>.migrateFilters` by Maintenance,
	// on any holder of a filter — its names written as the ids they mean.
	migrateFilters: 'Filters named by id',
	// The element-id backfill (KPR-168, keepr-api utils/elementIdBackfill.js):
	// `card.assignElementIds` / `elementSet.assignElementIds` by Maintenance,
	// listing each id it gave an element. Never revertable.
	assignElementIds: 'Element ids given',
	// Tiles in six columns (KPR-346): `cardLayout.migrateColumns` by
	// Maintenance — a tile layout's spans rewritten for its columns, the tile
	// reading as it did. Never revertable. The task that wrote these
	// (migrateTileColumns) was retired in KPR-761; its rows stay in History.
	migrateColumns: 'Columns rewritten',
};
const ACTION_LABELS = {
	'grant.create': 'Granted', 'grant.update': 'Grant changed', 'grant.delete': 'Grant revoked', 'grant.revert': 'Grant reverted',
	'maintenance.run': 'Maintenance run',
	// A card change (keepr-api KPR-702) and its revert (KPR-704): "Changecard" otherwise.
	'item.changeCard': 'Changed the card',
	// The ✕ on an item's file and its way back (keepr-api `attachment` type):
	// named for what a person did, like item.detach's "Removed file".
	'attachment.delete': 'Removed file', 'attachment.restore': 'Restored file',
	// The hard purge of a deleted account's collection, 30 days on.
	'collection.purge': 'Purged',
	// A card's purge of the values its items held under removed elements,
	// and the revert that puts them back (delete audit F9).
	'card.purgeValues': 'Purged values', 'card.restoreValues': 'Restored values',
	// The archive path of a global collection's retirement moved records
	// out (keepr-api delete audit F12): never revertable, copied back instead.
	'collection.move': 'Moved to archive',
	// An automatic tag's pass (T6a): one summary row on the tag, counts only.
	'tag.rulePass': 'Applied by a rule',
	// A merge (T9), and an item that stopped being a tag with both kinds of
	// its applications removed (T2c) — named as its ⋯ entry names the act.
	'tag.merge': 'Merged', 'item.tagOff': 'Stopped using as a tag',
	// A card's item-tag pass (T6b): named after the setting it carries out,
	// as a rule's pass is ("Applied by a rule"); a where-referenced pass reads
	// "Apply where referenced" (actionLabel's entry). ⚑ the words are the
	// settings' own (ruled d01), not a verb of their own.
	'collection.itemTagsPass': 'Use items as tags',
	// A manager's choice of the dashboard Overview shows for everyone (charts
	// A7, keepr-api utils/overviewUtils.js): never revertable — it is chosen
	// again from Overview. A person's own choice and their chart pins are
	// favorite rows, in their own feed only, labelled by the API ("a chart
	// pinned to Home", "the dashboard on your Overview").
	'collection.overview': 'Overview dashboard',
	// "Me too" (KPR-390): a person's own confirmation of an item and its
	// taking back — rows only the collection's managers, admins and the
	// person read, never revertable (keepr-api utils/changeLog/describe.js
	// "confirmed …" / "took back a confirmation of …").
	'item.confirm': 'Confirmed', 'item.unconfirm': 'Took back',
	// A manager downloading who confirmed (KPR-440): read by the same people,
	// never revertable; its one change is the API's sentence ("3 people, as a
	// file") — how many, never who.
	'item.exportConfirmations': 'Exported who confirmed',
};

/**
 * A collection tag's own row (docs/plans/tags.md §3.9): the API writes
 * `tag.*` rows with the tag's shape version, `v: 2`. A collection tag's
 * delete is restored from History like any other (`revertable` from the API).
 */
export const isRebuiltTagEntry = entry => entry?.target?.type === 'tag' && entry?.v === 2;

/** A rebuilt collection tag's deletion — its revert IS the tag's restore (keepr-api POST /api/tags/{id}/restore). */
export const isTagDeletionEntry = entry => isRebuiltTagEntry(entry) && entry.action === 'tag.delete';

/**
 * An item's tag applications in its History (docs/plans/tags.md §3.9): hand
 * applications are the `tagIds` array of an `item.update` row, and what a
 * rule applied is `tagAutoIds` (an `item.recompute` row, T6a). Both hold tag
 * ids; the API names each one LIVE, beside it (`fromDisplay` / `toDisplay`,
 * position for position — keepr-api describe.js `tagLabel`): its name where
 * the reader may read it, else one of its tombstone phrases. Private tags
 * never appear here — they write nothing.
 */
export const TAG_ARRAY_LABELS = Object.freeze({ tagIds: 'Tags', tagAutoIds: 'Applied by a rule' });
export const isTagArrayChange = change => Boolean(change && Object.prototype.hasOwnProperty.call(TAG_ARRAY_LABELS, change.path));
export const hasTagArrayChanges = entry => Array.isArray(entry?.changes) && entry.changes.some(isTagArrayChange);

// ------------------------------------------------------ tags, as tag badges

/**
 * The words keepr-api's describer prints for a tag it will not name
 * (utils/changeLog/describe.js DELETED_TAG / FOREIGN_TAG / UNAVAILABLE_TAG):
 * deleted (or never there), a tag of a collection the reader cannot see, an
 * item tag whose item the reader cannot read. Each is a TOMBSTONE here —
 * muted, and never a link (docs/platform-standards.md § Tags).
 */
export const HISTORY_TAG_TOMBSTONES = Object.freeze({
	deleted: DELETED_TAG_LABEL,
	foreign: 'a tag from another collection',
	unavailable: 'No longer available',
});
const TOMBSTONE_WORDS = new Set(Object.values(HISTORY_TAG_TOMBSTONES));
const TOMBSTONE_MANY = {
	[HISTORY_TAG_TOMBSTONES.deleted]: n => `${n} deleted tags`,
	[HISTORY_TAG_TOMBSTONES.foreign]: n => `${n} tags from other collections`,
	[HISTORY_TAG_TOMBSTONES.unavailable]: n => `${n} tags no longer available`,
};

/**
 * One side of a tag-array change as TAG BADGES (ruled b54, 2026-09-30):
 * `[{ key, tag, tombstone?, label? }]`, the named tags first, in the row's
 * order, then the tombstones, one per phrase with its count ("2 deleted
 * tags").
 *
 * An id is named, in order of preference, by
 *   1. the reader's vocabulary (`byId`, utils/tags.js tagsById — the
 *      collection's tags and its parent's): the tag itself, with its icon,
 *      color and marks;
 *   2. the API's name for it (`displays[i]`), which it prints only where the
 *      reader may read the tag — an item tag (its item's title; drawn as its
 *      item's badge when this session was shown the item's card,
 *      utils/tagTitleMemory.js), or a tag the vocabulary does not list;
 *   3. an item tag's title this session was shown, where the row carries no
 *      names (a revert's conflict list);
 * and otherwise it is a tombstone, in the API's words ("a deleted tag" when
 * there are none). A named tag's badge is the door to its tag page; a
 * tombstone never is.
 */
export function historyTagEntries(ids, displays, byId) {
	if (ids === undefined || ids === null) return [];
	const raw = Array.isArray(ids) ? ids : [ids];
	const names = Array.isArray(displays) ? displays : (displays === undefined || displays === null ? [] : [displays]);
	const named = [];
	const gone = new Map();
	raw.forEach((value, index) => {
		const id = normalizeId(value);
		if (!id) return;
		const known = byId && typeof byId.get === 'function' ? byId.get(id) : null;
		if (known) { named.push({ key: id, tag: known }); return; }
		const shown = typeof names[index] === 'string' ? names[index].trim() : '';
		const remembered = itemTagTitleFor(id);
		const title = shown && !TOMBSTONE_WORDS.has(shown) ? shown : (!shown && remembered?.title) || '';
		if (title) {
			named.push({
				key: id,
				tag: remembered
					? { _id: id, kind: 'item', title, card_id: remembered.card_id, ...(remembered.collection_id ? { collection_id: remembered.collection_id } : {}) }
					: { _id: id, name: title },
			});
			return;
		}
		const label = TOMBSTONE_WORDS.has(shown) ? shown : HISTORY_TAG_TOMBSTONES.deleted;
		gone.set(label, (gone.get(label) || 0) + 1);
	});
	const tombstones = [...gone.entries()].map(([label, n]) => ({
		key: `gone:${label}`,
		tag: null,
		tombstone: true,
		label: n === 1 ? label : TOMBSTONE_MANY[label](n),
	}));
	return [...named, ...tombstones];
}

/**
 * ONE tag a row names by id and by the name it had then — a tag row's target,
 * a merge's survivor. The reader's vocabulary is what says it is still there:
 * found, it is the tag itself (a link to its page); once the vocabulary has
 * answered without it (`loaded`), a tombstone that keeps the row's name;
 * before that, the name as the row gives it, not yet a link.
 */
export function historyTagRef(id, name, byId, { loaded = false } = {}) {
	const key = normalizeId(id);
	const known = key && byId && typeof byId.get === 'function' ? byId.get(key) : null;
	if (known) return { key, tag: known };
	const label = typeof name === 'string' && name.trim() ? name.trim() : HISTORY_TAG_TOMBSTONES.deleted;
	if (loaded || !key) return { key: key || `gone:${label}`, tag: null, tombstone: true, label };
	return { key, tag: { _id: key, name: label }, unconfirmed: true };
}

/** The words a list of those entries reads as ("Urgent, a deleted tag"). */
export const historyTagWords = entries => (entries || []).map(entry => {
	if (entry.tombstone) return entry.label;
	return entry.tag?.kind === 'item' ? entry.tag.title : entry.tag?.name;
}).filter(Boolean);

/**
 * AN AUTOMATIC TAG'S ROWS (T6a; keepr-api utils/changeLog/describe.js). A
 * rule set, changed or stopped on a tag is a `tag.update` row carrying a
 * `ruleEdit` summary (`{ change, strict?, handApplications?, automatic? }`);
 * each pass that made it true, paused it or stopped it is ONE `tag.rulePass`
 * row carrying `rulePass` (`{ kind, applied, removed, handRemoved,
 * movedToHand, full, reason?, stopped? }`) — counts, never item ids. Both come
 * with the API's sentence in `description`; `describeRuleChange` says the same
 * for a row that has none.
 *
 * A pass that was not a person's act on the rule says why it ran (`reason`,
 * keepr-api T6b): `daily` (a time-dependent rule's daily re-check),
 * `dependency` (a rule whose tag it reads changed) or `move` (items moved).
 * The API's sentence leaves it out, so it is added here — to the change's
 * words and as the entry's chip (`rulePassReason`).
 */
export const isRuleSummaryChange = change => Boolean(change && (change.ruleEdit || change.rulePass));

/** Why a rule's pass ran, as the change's words say it ("tagged 3 items, in the daily re-check"). */
export const RULE_PASS_REASON_WORDS = Object.freeze({
	daily: 'in the daily re-check',
	dependency: 'because another automatic tag changed',
	move: 'after items moved',
});

/** The same, as the entry's chip. */
const RULE_PASS_REASON_CHIPS = Object.freeze({
	daily: 'The daily re-check',
	dependency: 'Because another automatic tag changed',
	move: 'After items moved',
});

/** An entry's rule pass reason as its chip ("The daily re-check"), or null. */
export function rulePassReason(entry) {
	const changes = Array.isArray(entry?.changes) ? entry.changes : [];
	const pass = changes.find(change => change?.rulePass && RULE_PASS_REASON_CHIPS[change.rulePass.reason]);
	return pass ? RULE_PASS_REASON_CHIPS[pass.rulePass.reason] : null;
}

const withReasonWords = (words, reason) => (RULE_PASS_REASON_WORDS[reason] && words && !words.endsWith(RULE_PASS_REASON_WORDS[reason])
	? `${words}, ${RULE_PASS_REASON_WORDS[reason]}`
	: words);

const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export function describeRuleChange(change) {
	const e = change?.ruleEdit;
	if (e) {
		const words = {
			create: 'gave it a rule', reshape: 'changed its rule', strict: 'made it applied only by its rule',
			nonStrict: 'let people apply it too', stop: 'stopped applying it by rule',
		};
		const parts = [words[e.change] || 'changed its rule'];
		if (e.change === 'create' || e.change === 'reshape') parts.push(e.strict ? 'only the rule applies it' : 'people can apply it too');
		if (e.handApplications) parts.push(`its ${count(e.handApplications, 'hand application comes', 'hand applications come')} off`);
		if (e.change === 'stop' && e.automatic) parts.push(count(e.automatic, 'automatic application becomes a hand one', 'automatic applications become hand ones'));
		return parts.join('; ');
	}
	const r = change?.rulePass;
	if (r) {
		const parts = [];
		if (r.applied) parts.push(`tagged ${count(r.applied, 'item', 'items')}`);
		if (r.removed) parts.push(`took it off ${count(r.removed, 'item', 'items')}`);
		if (r.handRemoved) parts.push(`took ${count(r.handRemoved, 'hand application', 'hand applications')} off`);
		if (r.movedToHand) parts.push(count(r.movedToHand, 'automatic application became a hand one', 'automatic applications became hand ones'));
		if (r.full) parts.push(`${count(r.full, 'item', 'items')} already had 50 tags and ${r.full === 1 ? 'was' : 'were'} left without it`);
		if (r.stopped) parts.push('stopped before the end');
		return withReasonWords(parts.join('; ') || (r.kind === 'pause' ? 'nothing to take off' : 'nothing changed'), r.reason);
	}
	return '';
}

/**
 * THE TAGS' OTHER SUMMARIES (keepr-api utils/changeLog/describe.js, the wire
 * half of itemTagPhrase / mergePhrase / applicationsPhrase): counts and modes,
 * never item ids, each with the API's sentence in `description`.
 *
 *   applications  a collection tag's delete or restore: how many items
 *   tagMerge      a merge (`tag.merge`): the tag it went into, and counts
 *   tagConversion an item tag turned into a collection tag (`tag.update`)
 *   itemTag       an item that stopped being a tag (`item.tagOff`)
 *   itemTagMoved  a moved item's tag left items outside its new family
 *   itemTags      a card's Use items as tags changed (`collection.update`)
 *   whereReferenced  a card's Apply where referenced switched (or, on an
 *                 item, the where-referenced cap it passed)
 *   itemTagsPass  a card's item-tag pass (`collection.itemTagsPass`)
 *
 * Each reads as a sentence in the change table (ChangeTable), never as a
 * Before/After pair, and is never a revert target on its own.
 */
const TAG_SUMMARY_KEYS = ['applications', 'tagMerge', 'tagConversion', 'itemTag', 'itemTagMoved', 'itemTags', 'whereReferenced', 'itemTagsPass'];
export const tagSummaryKind = change => (change ? TAG_SUMMARY_KEYS.find(key => change[key] && typeof change[key] === 'object') || null : null);
export const isTagSummaryChange = change => Boolean(tagSummaryKind(change));

/** A card's Use items as tags choice, in the window's own words (Off · Chosen items · Every item). */
export const ITEM_TAG_MODE_WORDS = Object.freeze({ off: 'Off', chosen: 'Chosen items', every: 'Every item' });

const MAX_REFS = 500;

/**
 * One tag summary as SEGMENTS: strings, `{ card: <id> }` where the sentence
 * names a card (drawn as its record badge, or "a card"), and `{ tag: { id,
 * name } }` where it names a tag (a merge's survivor, drawn as its tag
 * badge). The card and the tag are named by the reader's own caches, never by
 * the API's sentence (which says "a card"); everything else is counts.
 */
export function tagSummarySegments(change) {
	const kind = tagSummaryKind(change);
	if (!kind) return [];
	const s = change[kind];
	const plain = text => [change.description || text];
	switch (kind) {
		case 'itemTags': {
			const out = ['Use items as tags for ', { card: s.cardId }, `: from ${ITEM_TAG_MODE_WORDS[s.from] || s.from || 'Off'} to ${ITEM_TAG_MODE_WORDS[s.to] || s.to || 'Off'}`];
			if (s.items) out.push(`, for ${count(s.items, 'item', 'items')}`);
			else if (s.tags) out.push(`, for ${count(s.tags, 'item tag', 'item tags')}`);
			return out;
		}
		case 'whereReferenced':
			if (typeof s.to === 'boolean') return ['Apply where referenced for ', { card: s.cardId }, `: turned ${s.to ? 'on' : 'off'}`];
			return plain(`refers to ${count(s.overflow || 0, 'more tagged item', 'more tagged items')} than the ${s.cap || MAX_REFS} whose tags it carries`);
		case 'itemTagsPass': {
			const leads = {
				backfill: ['gave the items of ', { card: s.cardId }, ' their tags'],
				off: ['turned off the item tags of ', { card: s.cardId }],
				whereReferenced: ['applied the item tags of ', { card: s.cardId }, ' where their items are referenced'],
				whereReferencedOff: ['took off what the item tags of ', { card: s.cardId }, ' applied where their items are referenced'],
			};
			const out = [...(leads[s.kind] || ['ran an item-tag pass for ', { card: s.cardId }])];
			const parts = [];
			if (s.created) parts.push(`made ${count(s.created, 'tag', 'tags')}`);
			if (s.converted) parts.push(`turned ${count(s.converted, 'tag', 'tags')} into collection tags`);
			if (s.deleted) parts.push(`deleted ${count(s.deleted, 'tag', 'tags')}`);
			if (s.applied) parts.push(`applied ${count(s.applied, 'tag', 'tags')}`);
			if (s.removed) parts.push(`took off ${count(s.removed, 'tag', 'tags')}`);
			if (s.overflowItems) parts.push(`${count(s.overflowItems, 'item refers', 'items refer')} to more than ${MAX_REFS} tagged items and ${s.overflowItems === 1 ? 'carries' : 'carry'} the first ${MAX_REFS}`);
			if (s.stopped) parts.push('stopped before the end');
			if (parts.length) out.push(`; ${parts.join('; ')}`);
			return out;
		}
		case 'tagMerge': {
			const out = ['into ', { tag: { id: s.into, name: s.intoName } }];
			const parts = [];
			if (s.items) parts.push(`moved to ${count(s.items, 'item', 'items')}`);
			if (s.children) parts.push(`${count(s.children, 'tag', 'tags')} under it moved too`);
			if (s.aliasesAdded) parts.push(`${count(s.aliasesAdded, 'name', 'names')} kept as ${s.aliasesAdded === 1 ? 'an alias' : 'aliases'}`);
			if (s.queries) parts.push(`${count(s.queries, 'filter or rule', 'filters and rules')} now ${s.queries === 1 ? 'names' : 'name'} it`);
			if (parts.length) out.push(`; ${parts.join('; ')}`);
			return out;
		}
		case 'applications':
			if (s.restored !== undefined) return plain(`put it back on ${count(s.restored || 0, 'item', 'items')}${s.skipped ? ` (${count(s.skipped, 'item', 'items')} could not take it back)` : ''}`);
			return plain(s.items ? `took it off ${count(s.items, 'item', 'items')}` : 'it was on no items');
		case 'tagConversion': {
			const parts = ['turned it into a collection tag'];
			if (s.handRemoved) parts.push(`took it off ${count(s.handRemoved, 'item', 'items')}`);
			if (s.autoMoved) parts.push(count(s.autoMoved, 'automatic application became a hand one', 'automatic applications became hand ones'));
			if (s.autoRemoved) parts.push(`removed ${count(s.autoRemoved, 'automatic application', 'automatic applications')}`);
			return plain(parts.join('; '));
		}
		case 'itemTag':
			return plain(s.items ? `took its tag off ${count(s.items, 'item', 'items')}` : 'its tag was on no items');
		case 'itemTagMoved': {
			const parts = [];
			if (s.items) parts.push(`its tag was taken off ${count(s.items, 'item', 'items')} outside its new collection`);
			if (s.parentCleared) parts.push('its tag left the tag it sat under');
			return plain(parts.join('; ') || 'its tag moved with it');
		}
		default:
			return plain('');
	}
}

/** A tag's `rule` as words: none (a hand tag), or who applies it (the strict choice, ruled d11). */
export function ruleValueWords(value) {
	if (value === null || value === undefined) return 'By hand';
	if (typeof value === 'object') return value.strict ? 'Only this rule applies this tag' : 'Applied by a rule · People can apply it too';
	return undefined;
}

/**
 * A rebuilt tag's row with its rule said in words: the `rule` diff (null ↔
 * `{ automation_id, strict }`) reads "By hand" / "Applied by a rule · …", an
 * `automation_id` never shows as an id, `rule.strict` is labelled "Only this
 * rule applies this tag", and a summary without the API's sentence gets one.
 * Every other entry is returned as it is.
 */
export function withTagRuleWords(entry) {
	if (!isRebuiltTagEntry(entry) || !Array.isArray(entry.changes)) return entry;
	if (!entry.changes.some(change => ['rule', 'rule.strict', 'rule.automation_id'].includes(change?.path) || isRuleSummaryChange(change))) return entry;
	return {
		...entry,
		changes: entry.changes.map(change => {
			if (isRuleSummaryChange(change)) {
				if (!change.description) return { ...change, description: describeRuleChange(change) };
				// The API's sentence has the counts, never why the pass ran.
				const described = change.rulePass ? withReasonWords(change.description, change.rulePass.reason) : change.description;
				return described === change.description ? change : { ...change, description: described };
			}
			if (change?.path === 'rule') {
				const out = { ...change, label: change.label || 'Rule' };
				const from = change.from === undefined ? undefined : ruleValueWords(change.from);
				const to = change.to === undefined ? undefined : ruleValueWords(change.to);
				if (from) out.fromDisplay = from;
				if (to) out.toDisplay = to;
				return out;
			}
			if (change?.path === 'rule.strict') {
				const out = { ...change, label: 'Only this rule applies this tag' };
				delete out.labelMissing;
				return out;
			}
			if (change?.path === 'rule.automation_id') {
				return { ...change, label: 'Applied by a rule', ...(change.from !== undefined ? { fromDisplay: change.from ? 'Yes' : 'No' } : {}), ...(change.to !== undefined ? { toDisplay: change.to ? 'Yes' : 'No' } : {}) };
			}
			return change;
		}),
	};
}

/**
 * Is this entry the deletion of an item — a delete, or an intake reject (the
 * same hard delete, keepr-api `item.reject`)? Its item has no page while it is
 * gone, so the entry is the door to the rest of its history.
 */
export const isItemDeletion = entry => entry?.target?.type === 'item' && entry?.op === 'delete';

/**
 * The short verb a tag carries for an action ("Updated", "Granted",
 * "Restored"). `entry`, where the caller has it, lets a row's own summary
 * pick the words (a card's where-referenced pass).
 */
export function actionLabel(action, entry = null) {
	if (!action) return 'Changed';
	if (action === 'collection.itemTagsPass' && Array.isArray(entry?.changes)
		&& entry.changes.some(change => /^whereReferenced/.test(change?.itemTagsPass?.kind || ''))) {
		return 'Apply where referenced';
	}
	if (ACTION_LABELS[action]) return ACTION_LABELS[action];
	const verb = String(action).split('.')[1];
	return VERBS[verb] || (verb ? verb[0].toUpperCase() + verb.slice(1) : 'Changed');
}

/** Tag color by what kind of thing happened: creates green, deletes red, reverts purple, the rest blue; derived grey. */
export function actionColor(entry) {
	if (!entry) return 'default';
	if (entry.derived) return 'default';
	if (entry.revert || /\.revert$/.test(entry.action || '')) return 'purple';
	if (/\.(restore|restoreValues)$/.test(entry.action || '')) return 'cyan';
	if (entry.op === 'create') return 'green';
	if (entry.op === 'delete' || /\.(delete|revoke|unrelate|detach|remove|purgeValues)$/.test(entry.action || '')) return 'red';
	return 'blue';
}

/** Who did it, as one phrase — the client-side twin of the server's actor naming. */
export function actorLabel(actor) {
	if (!actor) return 'Someone';
	const name = actor.user?.fullName
		|| [actor.user?.firstName, actor.user?.lastName].filter(Boolean).join(' ')
		|| null;
	switch (actor.type) {
		case 'automation':
			return `Automation${actor.automationName ? ` “${actor.automationName}”` : ''}`;
		case 'api-key':
			return name ? `${name} (via API key${actor.keyName ? ` “${actor.keyName}”` : ''})` : 'An API key';
		case 'maintenance':
			return name ? `${name} (maintenance)` : 'Maintenance';
		case 'scheduler':
			return 'The scheduler';
		case 'system':
			return 'The system';
		// An intake submission: nobody with an account, through a share link.
		case 'public':
			return 'A visitor, through an intake link';
		default:
			return name || 'Someone';
	}
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;
const HEX_24 = /^[0-9a-f]{24}$/i;

/**
 * A stored value as cell text. A display form (choice label, lookup title)
 * wins over the raw value; dates render through the display preference;
 * empties are an em dash rather than a blank so "cleared" is visible.
 */
export function formatChangeValue(value, display) {
	if (display !== undefined && display !== null && display !== '') {
		return Array.isArray(display) ? display.map(d => `${d}`).join(', ') : `${display}`;
	}
	if (value === undefined || value === null || value === '') return '—';
	// A yes/no element whose card author renamed its states ("Paid" /
	// "Unpaid") arrives with the words already in `display` — the API knows
	// the element, a change row does not. Yes / No is the fallback.
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	if (typeof value === 'number') return `${value}`;
	if (typeof value === 'string') {
		if (ISO_DATE.test(value)) {
			return value.length > 10 ? formatDisplayDateTime(value) : formatDisplayDate(value);
		}
		return value;
	}
	if (Array.isArray(value)) {
		if (!value.length) return '—';
		return value.map(v => formatChangeValue(v)).join(', ');
	}
	if (typeof value === 'object') {
		// A location is ONE value in the change log (the API records a move
		// as "Town Hall -> Old Mill", not three sub-path rows), so it renders
		// as the place it names rather than as its JSON. isLocationValue is
		// the shared module's own answer to "is this that shape?" — a change
		// row has the value but not the element definition.
		if (isLocationValue(value)) return formatLocation(value) || '—';
		// Money reads as money ("CA$12.00"): without the element's list the
		// formatter prints each currency's unambiguous symbol.
		if (isCurrencyValue(value)) return formatCurrency(value) || '—';
		if (value.label && typeof value.label === 'object') return `${value.label.singular || value.label.plural || ''}`;
		if (typeof value.label === 'string') return value.label;
		if (typeof value.name === 'string') return value.name;
		if (typeof value.cardDefinitionId === 'string' && HEX_24.test(value.cardDefinitionId)) return value.name || value.cardDefinitionId;
		const json = JSON.stringify(value);
		return json.length > 120 ? `${json.slice(0, 119)}…` : json;
	}
	return `${value}`;
}

/**
 * A repeating group's change, recorded ENTRY BY ENTRY (keepr-api KPR-678,
 * docs/SCHEMA.md § History): `entry` says which group and entry, and where it
 * stood when the change was written — `{ group, id, position, number,
 * heading?, column?, section?, op? }`. The API's `label` already reads as a
 * person counts entries ("Ingredient 3 · Amount"), so a row draws it as it
 * draws any change; this tells the few places that differ.
 */
export const isEntryChange = change => Boolean(change && change.entry && typeof change.entry === 'object');
/** A repeating group's order: the entries that MOVED (`moved`), said as the API's sentence. */
export const isEntryMove = change => Boolean(change && change.reorder && Array.isArray(change.moved));

/**
 * One entry as cell text, where the API sent no display form (a revert's
 * conflict): a heading's title, else its values joined by " · ", in the
 * order they are stored (the group's column order). An empty entry is a dash.
 */
export function formatEntryValue(value) {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return formatChangeValue(value);
	if (typeof value.section === 'string') return value.section || '—';
	const values = value.elements && typeof value.elements === 'object' ? Object.values(value.elements) : [];
	const parts = values.map(v => formatChangeValue(v)).filter(text => text !== '—');
	return parts.length ? parts.join(' · ') : '—';
}

/** What a change row says happened to its path, when it is not a plain before→after. */
export function changeKind(change) {
	if (!change) return 'value';
	if (change.redacted) return 'redacted';
	if (change.reorder) return 'reorder';
	const hasFrom = change.from !== undefined;
	const hasTo = change.to !== undefined;
	if (Number.isInteger(change.at) && /\]$/.test(change.path || '')) return hasTo && !hasFrom ? 'added' : 'removed';
	if (hasTo && !hasFrom) return 'set';
	if (hasFrom && !hasTo) return 'cleared';
	return 'value';
}
